import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

const JITSI_DOMAIN = "meet.okotech.ai";

/**
 * The one Jitsi wrapper for every live session type (meeting, interview,
 * eKYC, healthcare) — a superset of the four previously-separate
 * JitsiMeeting.jsx implementations, each of which needed genuinely
 * different Jitsi behavior:
 *   - eKYC needs an imperative ref (captureScreenshot/getApi/
 *     getLocalVideoStream) for its avatar-guided verification steps.
 *   - Interview auto-opens chat+participants on join, disables the
 *     filmstrip, restricts the toolbar, and tracks dominant-speaker/
 *     audio-level/remote-track events for its answer-scoring pipeline.
 *   - Healthcare renders its own participants list from live roster events.
 * Rather than force one fixed config, every behavior here is opt-in via
 * props — SessionRoom picks the right ones per type from sessionTypes.js,
 * so each type keeps its exact existing Jitsi behavior.
 */
const JitsiSession = forwardRef(({
  roomName, displayName, userInfo,
  scriptSrc = "https://meet.okotech.ai/external_api.js",
  configOverwrite = {}, interfaceConfigOverwrite = {},
  autoOpenChat, autoOpenParticipants, chatOpen,
  onLeave, onJoined, onButtonClick, onSpeakerChange, onRemoteAudio,
  onParticipantsJoin, onParticipantsListChange, onParticipantsUpdate,
  mouseEventTriggered,
}, ref) => {
  const containerRef      = useRef(null);
  const apiRef            = useRef(null);
  const previousChatState = useRef(null);

  useImperativeHandle(ref, () => ({
    captureScreenshot: async () => (apiRef.current ? apiRef.current.captureLargeVideoScreenshot() : null),
    getApi: () => apiRef.current,
    getLocalVideoStream: () => {
      if (apiRef.current && apiRef.current._conference) {
        const tracks = apiRef.current._conference.getLocalTracks();
        const videoTrack = tracks.find((t) => t.getType() === "video");
        if (videoTrack) return new MediaStream([videoTrack.getOriginalStream().getVideoTracks()[0]]);
      }
      return null;
    },
  }));

  useEffect(() => {
    let cancelled = false;
    let lastSpeaker = null;

    const mount = () => {
      if (cancelled || !window.JitsiMeetExternalAPI) return;

      const api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: userInfo || { displayName },
        configOverwrite,
        interfaceConfigOverwrite,
      });
      apiRef.current = api;

      api.addEventListener("videoConferenceJoined", (event) => {
        if (autoOpenChat) api.executeCommand("toggleChat");
        if (autoOpenParticipants) api.executeCommand("toggleParticipantsPane");
        onJoined?.(event);
        onButtonClick?.(event.displayName || displayName || "Unknown");
      });

      const reportSpeaker = (id) => {
        if (!onSpeakerChange || !id) return;
        const name = id === "local"
          ? (displayName || "You")
          : (api.getParticipantsInfo().find((p) => p.participantId === id)?.displayName || "Unknown speaker");
        if (name !== lastSpeaker) { lastSpeaker = name; onSpeakerChange(name); }
      };
      api.addEventListener("dominantSpeakerChanged", (event) => reportSpeaker(event.id || event.participantId));
      api.addEventListener("audioLevelsChanged", (levels) => {
        levels.forEach(({ participantId, level }) => { if (level > 0.5) reportSpeaker(participantId); });
      });

      const relayRemoteAudio = (track) => {
        if (!onRemoteAudio || track.isLocal() || track.getType() !== "audio") return;
        onRemoteAudio(new MediaStream([track.getTrack()]));
      };
      api.addEventListener("remoteTrackAdded", relayRemoteAudio);
      api.addEventListener("trackAdded", relayRemoteAudio);

      const notifyParticipants = () => {
        const list = api.getParticipantsInfo();
        const names = Array.from(new Set(list.map((p) => p.displayName || "Unknown")));
        if (names.length >= 2) onParticipantsJoin?.(names.length);
        onParticipantsListChange?.(names);
        onParticipantsUpdate?.(list);
      };
      api.addEventListener("participantJoined", notifyParticipants);
      api.addEventListener("participantLeft", notifyParticipants);
      api.addEventListener("participantRoleChanged", notifyParticipants);
      api.addEventListener("videoConferenceJoined", notifyParticipants);

      api.addEventListener("mouseMove", (event) => mouseEventTriggered?.(event.event.clientX));

      api.addListener("videoConferenceLeft", () => onLeave?.());
      api.addListener("readyToClose", () => onLeave?.());
    };

    let script = document.querySelector(`script[src="${scriptSrc}"]`);
    if (window.JitsiMeetExternalAPI) {
      mount();
    } else {
      if (!script) {
        script = document.createElement("script");
        script.src = scriptSrc;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener("load", mount);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener("load", mount);
      if (apiRef.current) {
        try { apiRef.current.dispose(); } catch (e) { /* already gone */ }
        apiRef.current = null;
      }
    };
    // Only remount the Jitsi iframe when the room/identity actually changes —
    // matches every prior wrapper's behavior (callbacks are read fresh via
    // closures each mount, not tracked as remount triggers).
  }, [roomName, displayName, scriptSrc]);

  // Interview's pattern: toggle Jitsi's own chat panel when the caller's
  // own dock state changes, so the two don't visually overlap.
  useEffect(() => {
    if (!apiRef.current || chatOpen === undefined) return;
    if (previousChatState.current !== chatOpen) {
      apiRef.current.executeCommand("toggleChat");
      previousChatState.current = chatOpen;
    }
  }, [chatOpen]);

  return <div ref={containerRef} style={{ width: "100%", height: "100vh" }} />;
});

export default JitsiSession;
